import {
  Box,
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BoardIcon,
  CreateBoardIcon,
  CreateNewIcon,
  DeleteIcon,
  EditIcon,
  EllipsisIcon,
  EllipsisIconLight,
  LockFolder,
  OpenFolder,
  ShareIcon,
  VisibleIcon,
} from "../../assets/Icons";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  BlurredOverlay,
  FolderListItem,
} from "../../pageComponents/auth/style";
import useConfig from "../../hooks/useConfig";
import { folderItem, selectFolderBoardItem } from "../../store/metadata/types";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteFolderLoadingSelector,
  folderListsSelector,
  selectedTabIndexSelector,
  selectFolderDetailsSelector,
  selectFolderSelector,
  teamFolderItemSelector,
} from "../../store/metadata/selector";
import { MetaDataReducerAction } from "../../store/metadata/slice";
import routeConstant from "../../utils/routeConstant";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "styled-components";
import { SwipeFileReducerAction } from "../../store/swipeFile/slice";
import { useEffect, useRef, useState } from "react";
import { MetaDataSagaActions } from "../../store/metadata/sagas";
import { defaultFolderName } from "../../utils/constants";
import useAuth from "../../hooks/useAuth";
import DeleteConfirmationModal from "../modals/deleteConfirmationModal";
import { toast } from "react-toastify";
import { activeSubscriptionSelector } from "../../store/payment/selector";
import { EyeIcon, EyeIconButton } from "../commonComponents/formFields/style";
import { getPlanAccess } from "../../utils/planAccess";
import { userDetailsSelector } from "../../store/account/selector";

const SidebarTeamsFolders = () => {
  const { isAuthenticated } = useAuth();
  const { drawerOpen } = useConfig();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const folderList = useSelector(folderListsSelector);
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedFolder = useSelector(selectFolderSelector);
  // const selectedFolderItem = useSelector(folderItemSelector);
  const selectedFolderItem = useSelector(teamFolderItemSelector);
  const selectedFolderDetails = useSelector(selectFolderDetailsSelector);
  const selectedTabIndex = useSelector(selectedTabIndexSelector);
  const activePlan = useSelector(activeSubscriptionSelector);
  const userDetails = useSelector(userDetailsSelector);
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [selectedActionFolder, setSelectedActionFolder] =
    useState<folderItem>();

  const folderListToShow = isAuthenticated
    ? folderList?.teamFolders
    : selectedFolderDetails
    ? [selectedFolderDetails]
    : [];
  const selectedFolderToShow = isAuthenticated
    ? selectedFolder
    : selectedFolderDetails;

  const [prevFolderCount, setPrevFolderCount] = useState(folderListToShow);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isDeleteItemFolder, setIsDeleteItemFolder] = useState<boolean>(false);
  const [isEllipsesOpen, setIsEllipsesOpen] = useState<string | null>(null);
  const accessConfig = getPlanAccess(
    activePlan?.activePlanDetails?.planType ||
      userDetails?.parentPlanDetails?.planType
  );
  const loading = useSelector(deleteFolderLoadingSelector);

  useEffect(() => {
    if (selectedFolderDetails) {
      setIsEllipsesOpen(selectedFolderDetails._id);
    }
  }, [selectedFolderDetails]);

  // Add this new useEffect to handle folder highlighting after page refresh
  useEffect(() => {
    // Get the folder ID from the URL
    const urlFolderId = location.pathname.split("/")[2];

    // If we're on a folder page and have a selected folder, ensure it's highlighted
    if (
      urlFolderId &&
      selectedFolderToShow &&
      selectedFolderToShow?._id === urlFolderId
    ) {
      setIsEllipsesOpen(urlFolderId);
    }
  }, [location.pathname, selectedFolderToShow]);

  const handleAnClick = (
    event: React.MouseEvent<HTMLElement>,
    item?: folderItem,
    board?: any,
    index?: number
  ) => {
    const newItem = Object.assign({}, item, {
      selectedBoard: board,
      selectedIndex: index,
    });

    setIsDeleteItemFolder(!board);

    setAnchorEl(event.currentTarget);
    dispatch(MetaDataReducerAction.resetSkipLimitValue());
    // dispatch(MetaDataReducerAction.setFolderItem(newItem));
    dispatch(MetaDataReducerAction.setTeamFolderItem(newItem));
    setSelectedActionFolder(newItem);
  };

  const handleClose = () => {
    setAnchorEl(null);
    // dispatch(MetaDataReducerAction.setFolderItem(null));
    dispatch(MetaDataReducerAction.setTeamFolderItem(null));
  };

  const handleMenuItemClick = () => {
    dispatch(
      MetaDataReducerAction.setIsRemoveUserModalOpen({
        isOpen: true,
        isUser: false,
        shareType: "team",
        folderId: selectedActionFolder?._id,
      })
    );
    setAnchorEl(null);
  };

  const handleEditFolder = () => {
    dispatch(MetaDataReducerAction.setFolderItem(selectedActionFolder));
    dispatch(MetaDataReducerAction.setOpenTeamCreateFolder(true));
    setAnchorEl(null);
  };

  const handleViewFolder = () => {
    if (selectedFolderItem?.selectedBoard) {
      handleClick?.(
        selectedFolderItem?.selectedBoard?._id,
        selectedFolderItem,
        selectedFolderItem?.selectedIndex
      );
    } else {
      dispatch(MetaDataReducerAction.resetSwipefileState());
      dispatch(
        MetaDataReducerAction.setSelectedFolderId(selectedActionFolder?._id)
      );
      dispatch(MetaDataReducerAction.setSelectedFolder(selectedActionFolder));
      dispatch(MetaDataReducerAction.setSelectedTabIndex(0));

      const { _id } = selectedFolderItem;
      navigate(`${routeConstant.myFolders}/${_id}`, {
        state: {
          type: "sidebarFolder",
        },
      });
    }
    handleClose();
  };

  const handleCopyLink = (selectedItem: any) => {
    window.navigator.clipboard.writeText(
      `${window.location.origin}${routeConstant.myFolders}/${selectedItem._id}`
    );
    toast.success("Link copied to clipboard");
    handleClose();
  };

  const handleCreateBoard = () => {
    dispatch(
      MetaDataReducerAction.setOpenBoardModal({
        isOpen: true,
        folderId: selectedActionFolder?._id,
      })
    );
    handleClose();
  };
  const handleClick = (
    boardId: string,
    folderItem: folderItem,
    index: number
  ) => {
    dispatch(MetaDataReducerAction.setSelectedFolder(folderItem));
    dispatch(SwipeFileReducerAction.setSelectedSidebarBoardId(boardId));
    dispatch(MetaDataReducerAction.setSelectedTabIndex(index));
    dispatch(MetaDataReducerAction.resetSwipefileState());
    localStorage.setItem("boardTabIndex", "0");

    dispatch(
      MetaDataSagaActions.fetchFolderListByBoardId({
        selectedFolderId: folderItem._id,
        selectedTabIndex: index,
        boardId: boardId,
      })
    );

    navigate(`${routeConstant.myFolders}/${folderItem._id}`, {
      state: {
        type: "sidebarFolder",
      },
    });
  };

  const closeAnchorElAfterDelete = () => {
    setAnchorEl(null);
  };
  const closeIsDeleteModalOpen = () => {
    setIsDeleteModalOpen(false);
  };
  const handleDelete = () => {
    if (selectedFolderItem?.selectedBoard) {
      dispatch(
        MetaDataSagaActions.deleteBoard({
          boardId: selectedFolderItem?.selectedBoard?._id,
          folderId: selectedFolderItem?._id,
          resetState: closeIsDeleteModalOpen,
        })
      );
    } else {
      // Set the folderItem in the state so the saga can access it
      dispatch(MetaDataReducerAction.setFolderItem(selectedFolderItem));
      dispatch(
        MetaDataSagaActions.deleteFolder({
          closeAnchorElAfterDelete: () => {
            closeIsDeleteModalOpen();
            // Only navigate if currently viewing a board from the deleted folder in My Folder
            const currentPath = location.pathname;
            const deletedFolderId = selectedFolderItem?._id;
            const isMyFolder = currentPath?.startsWith(routeConstant.myFolders);
            const currentFolderId = currentPath?.split("/")[2];
            if (isMyFolder && currentFolderId === deletedFolderId) {
              navigate(routeConstant.dashboard);
            }
          },
        })
      );
    }
    closeAnchorElAfterDelete();
  };

  const selectedFolderId = location.pathname.split("/")[2];

  useEffect(() => {
    const currentFolderCount = folderList?.teamFolders?.length || 0;

    // Only scroll to bottom if a new folder is added after the initial load
    if (
      currentFolderCount > prevFolderCount &&
      prevFolderCount !== 0 &&
      scrollRef.current
    ) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }

    // Update the previous folder count
    setPrevFolderCount(currentFolderCount);
  }, [folderList?.teamFolders]);

  const handleBlurSectionClick = () => {
    if (!isAuthenticated) {
      dispatch(MetaDataReducerAction.setOpenRegisterNowModal(true));
    }
  };

  const scrollToCenter = (folderId: string) => {
    setTimeout(() => {
      const element = document.getElementById(folderId);

      if (scrollRef.current && element) {
        const parentScrollEndAt =
          scrollRef.current.scrollTop + scrollRef.current.clientHeight;
        const elementTopWidthParent =
          element.offsetTop - scrollRef.current.offsetTop;
        if (elementTopWidthParent < scrollRef.current.scrollTop) {
          scrollRef.current.scrollTo({
            top: elementTopWidthParent,
            behavior: "smooth",
          });
          return;
        }
        if (element.clientHeight + elementTopWidthParent <= parentScrollEndAt) {
        } else {
          if (element.clientHeight >= scrollRef.current.clientHeight) {
            scrollRef.current.scrollTo({
              top: elementTopWidthParent,
              behavior: "smooth",
            });
          } else {
            const scrollByPoints =
              element.clientHeight + elementTopWidthParent - parentScrollEndAt;
            scrollRef.current.scrollBy({
              top: scrollByPoints,
              behavior: "smooth",
            });
          }
        }
      }
    }, 200);
  };
  return (
    <>
      <Divider sx={{ borderColor: "#2f4757" }} />
      <Box sx={{ position: "relative" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            margin: "1.5rem 1.5rem 0.5rem 1.8rem",
          }}
        >
          <span>Team Folders</span>
          <div
            onClick={() => {
              if (accessConfig.planType === "Free") {
                dispatch(MetaDataReducerAction.setOpenUpgradePlanModal(true));
              } else {
                dispatch(MetaDataReducerAction.setOpenTeamCreateFolder(true));
                dispatch(MetaDataReducerAction.setTeamFolderItem(null));
              }
            }}
            style={{
              cursor:
                accessConfig.planType === "Free" ? "not-allowed" : "pointer",
              opacity: accessConfig.planType === "Free" ? 0.6 : 1,
            }}
          >
            <CreateNewIcon fill="white" />
          </div>
        </div>
      </Box>

      <Box
        ref={scrollRef}
        sx={{
          height: "100%",
          maxHeight: {
            xs: "34.8vh",
            sm: "28.9vh",
            md: "23.7vh",
            lg: "15vh",
          },
          overflowY: "auto",
          overflowX: "hidden",
          "::-webkit-scrollbar": {
            width: "4px",
          },
          minHeight: "15vh",
        }}
      >
        <Box
          sx={{
            marginTop: "-0.5rem",
          }}
        >
          {folderListToShow?.length > 0 &&
            folderListToShow?.map((folderItem: folderItem, index: number) => (
              <div id={folderItem?._id} key={folderItem._id}>
                <FolderListItem
                  key={folderItem._id}
                  style={{
                    borderRadius: "4px",
                    background:
                      isEllipsesOpen === folderItem?._id
                        ? "linear-gradient(94.04deg, #1c75bc 2.44%, #00aeef 112.73%)"
                        : "transparent",
                    opacity: activePlan?.isFreePlan ? 0.6 : 1,
                  }}
                >
                  {folderItem?.isPrivate ? <LockFolder /> : <OpenFolder />}
                  {/* {folderItem?.isTeamShared || index === 1 ? (
                      <Groups2Icon fontSize="small" />
                    ) : null} */}
                  {drawerOpen && (
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div
                        onClick={() => {
                          if (
                            !activePlan?.isFreePlan &&
                            isEllipsesOpen !== folderItem?._id
                          ) {
                            setIsEllipsesOpen(folderItem?._id);
                            scrollToCenter(folderItem?._id);
                            // setLocalSelectedFolder(folderItem);
                            // dispatch(
                            //   MetaDataReducerAction.setSelectedFolder(item)
                            // );
                            // dispatch(
                            //   MetaDataReducerAction.setSelectedTabIndex(0)
                            // );
                            // dispatch(
                            //   MetaDataReducerAction.resetSwipefileState()
                            // );
                            // dispatch(
                            //   MetaDataSagaActions.fetchFolderListByBoardId({
                            //     selectedFolderId: item?._id,
                            //     selectedTabIndex: 0,
                            //     boardId: item?.boards[0]?._id,
                            //   })
                            // );
                          } else {
                            setIsEllipsesOpen(null);
                          }
                        }}
                        style={{
                          marginLeft: 10,
                          cursor:
                            selectedFolderId !== folderItem?._id
                              ? "pointer"
                              : "default",
                          color: "white",
                          textDecoration: "none",
                        }}
                      >
                        {folderItem.name}
                      </div>
                      <Box
                        sx={{
                          height: "100%",
                          px: 1,
                          cursor: activePlan?.isFreePlan
                            ? "not-allowed"
                            : "pointer",
                          pointerEvents: activePlan?.isFreePlan
                            ? "none"
                            : "auto",
                        }}
                        onClick={() => {
                          if (isEllipsesOpen !== folderItem?._id) {
                            setIsEllipsesOpen(folderItem?._id);
                            scrollToCenter(folderItem?._id);
                          } else {
                            setIsEllipsesOpen(null);
                          }
                        }}
                      >
                        {folderItem?.boards?.length ? (
                          !(isEllipsesOpen === folderItem?._id) ? (
                            <ArrowDownIcon color="white" />
                          ) : (
                            <ArrowUpIcon color="white" />
                          )
                        ) : null}
                      </Box>

                      {isAuthenticated &&
                        (activePlan?.isFreePlan ? (
                          <div
                            style={{
                              cursor: "pointer",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <EyeIconButton
                              onClick={(e) => {
                                e.preventDefault();
                                dispatch(
                                  MetaDataReducerAction.setOpenUpgradePlanModal(
                                    true
                                  )
                                );
                              }}
                            >
                              <EyeIcon
                                src={"/assets/images/eye-icon.svg"}
                                alt=""
                                style={{
                                  width: "26px",
                                  height: "26px",
                                }}
                              />
                            </EyeIconButton>
                          </div>
                        ) : (
                          <div
                            onClick={(event) =>
                              handleAnClick(event, folderItem)
                            }
                            style={{
                              cursor: "pointer",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            {selectedFolderToShow?._id === folderItem?._id ? (
                              <EllipsisIconLight />
                            ) : (
                              <EllipsisIcon />
                            )}
                          </div>
                        ))}
                    </div>
                  )}
                </FolderListItem>

                <Collapse
                  in={isEllipsesOpen === folderItem?._id}
                  timeout="auto"
                  unmountOnExit
                >
                  <span
                    style={{
                      marginLeft: "2.5rem",
                      marginBottom: "0.625rem",
                      display: "block",
                      cursor: "pointer",
                    }}
                  >
                    {folderItem?.boards && folderItem?.boards?.length > 0
                      ? "Boards"
                      : "No Boards"}
                  </span>
                  {folderItem?.boards?.map(
                    (boardItem: selectFolderBoardItem, index: number) => (
                      <List
                        key={boardItem?._id}
                        style={{ display: "flex" }}
                        component="div"
                        disablePadding
                      >
                        <ListItemButton
                          sx={{
                            pl: 4,
                            gap: "0.188rem",
                            paddingLeft: "0.625rem",
                            borderLeft: "1px solid #ffff",
                            marginLeft: "2.625rem",
                            paddingTop: "0.625rem",
                            cursor: "pointer",
                            fontWeight: "bolder",
                            color:
                              folderItem?._id === selectedFolderId &&
                              selectedTabIndex === index
                                ? theme.colors.lightBlue
                                : "white",
                          }}
                          onClick={() =>
                            handleClick(boardItem?._id, folderItem, index)
                          }
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: "1.5rem",
                              marginLeft: "0.75rem",
                            }}
                          >
                            <BoardIcon fill="white" />
                          </ListItemIcon>
                          <ListItemText
                            primary={boardItem?.name}
                            sx={{
                              cursor: "pointer",
                            }}
                          />
                        </ListItemButton>

                        {isAuthenticated && (
                          <div
                            onClick={(event) =>
                              handleAnClick(event, folderItem, boardItem, index)
                            }
                            style={{
                              cursor: "pointer",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              paddingRight: "10px",
                            }}
                          >
                            <EllipsisIcon />
                          </div>
                        )}
                      </List>
                    )
                  )}
                </Collapse>
              </div>
            ))}
        </Box>
      </Box>
      <DeleteConfirmationModal
        handleDelete={handleDelete}
        handleClose={() => setIsDeleteModalOpen(false)}
        isOpen={isDeleteModalOpen}
        loading={loading}
        modalTitle={`Do you want to delete the ${
          isDeleteItemFolder ? "folder" : "board"
        }?`}
        modalDescription={
          isDeleteItemFolder
            ? "This will delete the selected folder and all the boards inside it."
            : "This will delete the selected board and all the ads saved inside it."
        }
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        sx={{
          ".MuiMenu-paper": {
            borderRadius: "0.625rem",
          },
          ".MuiList-root": {
            padding: "0",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Create Board - Only if no board is selected and not Default */}
        {selectedFolderItem?.isFolderOwner &&
          !selectedActionFolder?.selectedBoard && (
            <MenuItem
              onClick={handleCreateBoard}
              style={{
                gap: "0.75rem",
                padding: "14px 15px",
                borderBottom: "1px solid #E3E6EA",
              }}
            >
              <CreateBoardIcon />
              <Typography
                fontSize="1rem"
                fontWeight="500"
                color={theme.colors.black2}
                style={{ cursor: "pointer" }}
              >
                Create Board
              </Typography>
            </MenuItem>
          )}

        {/* Default Folder - Show limited options */}
        {selectedActionFolder?.name === defaultFolderName ? (
          selectedActionFolder?.selectedBoard ? (
            // Board in Default Folder — show only View
            <MenuItem
              onClick={handleViewFolder}
              style={{
                gap: "0.75rem",
                padding: "14px 15px",
                borderBottom: "1px solid #E3E6EA",
              }}
            >
              <VisibleIcon />
              <Typography
                fontSize="1rem"
                fontWeight="500"
                color={theme.colors.black2}
                style={{ cursor: "pointer" }}
              >
                View
              </Typography>
            </MenuItem>
          ) : (
            <>
              {/* Folder in Default Folder — show View, Create, Copy Link, Share */}
              <MenuItem
                onClick={handleViewFolder}
                style={{
                  gap: "0.75rem",
                  padding: "14px 15px",
                  borderBottom: "1px solid #E3E6EA",
                }}
              >
                <VisibleIcon />
                <Typography
                  fontSize="1rem"
                  fontWeight="500"
                  color={theme.colors.black2}
                  style={{ cursor: "pointer" }}
                >
                  View
                </Typography>
              </MenuItem>

              <MenuItem
                onClick={() => handleCopyLink(selectedFolderItem)}
                style={{
                  gap: "0.75rem",
                  padding: "14px 15px",
                  borderBottom: "1px solid #E3E6EA",
                  color: theme.colors.gray1,
                }}
              >
                <ContentCopyIcon color="inherit" fontSize="small" />
                <Typography
                  fontSize="1rem"
                  fontWeight="500"
                  color={theme.colors.black2}
                  style={{ cursor: "pointer" }}
                >
                  Copy Public Link
                </Typography>
              </MenuItem>

              {selectedFolderItem?.isFolderOwner ? (
                <MenuItem
                  onClick={handleMenuItemClick}
                  style={{
                    gap: "0.75rem",
                    padding: "14px 15px",
                  }}
                >
                  <ShareIcon />
                  <Typography
                    fontSize="1rem"
                    fontWeight="500"
                    color={theme.colors.black2}
                    style={{ cursor: "pointer" }}
                  >
                    Share
                  </Typography>
                </MenuItem>
              ) : null}
            </>
          )
        ) : (
          // All other folders - show full options
          <>
            <MenuItem
              onClick={handleViewFolder}
              style={{
                gap: "0.75rem",
                padding: "14px 15px",
                borderBottom: "1px solid #E3E6EA",
              }}
            >
              <VisibleIcon />
              <Typography
                fontSize="1rem"
                fontWeight="500"
                color={theme.colors.black2}
                style={{ cursor: "pointer" }}
              >
                View
              </Typography>
            </MenuItem>

            {selectedFolderItem?.isFolderOwner ? (
              <MenuItem
                onClick={handleEditFolder}
                style={{
                  gap: "0.75rem",
                  padding: "14px 15px",
                  borderBottom: "1px solid #E3E6EA",
                }}
              >
                <EditIcon />
                <Typography
                  fontSize="1rem"
                  fontWeight="500"
                  color={theme.colors.black2}
                  style={{ cursor: "pointer" }}
                >
                  Edit
                </Typography>
              </MenuItem>
            ) : null}

            {!selectedActionFolder?.selectedBoard && (
              <>
                <MenuItem
                  onClick={() => handleCopyLink(selectedFolderItem)}
                  style={{
                    gap: "0.75rem",
                    padding: "14px 15px",
                    borderBottom: "1px solid #E3E6EA",
                    color: theme.colors.gray1,
                  }}
                >
                  <ContentCopyIcon color="inherit" fontSize="small" />
                  <Typography
                    fontSize="1rem"
                    fontWeight="500"
                    color={theme.colors.black2}
                    style={{ cursor: "pointer" }}
                  >
                    Copy Public Link
                  </Typography>
                </MenuItem>

                {selectedFolderItem?.isFolderOwner ? (
                  <MenuItem
                    onClick={handleMenuItemClick}
                    style={{
                      gap: "0.75rem",
                      padding: "14px 15px",
                    }}
                  >
                    <ShareIcon />
                    <Typography
                      fontSize="1rem"
                      fontWeight="500"
                      color={theme.colors.black2}
                      style={{ cursor: "pointer" }}
                    >
                      Share
                    </Typography>
                  </MenuItem>
                ) : null}
              </>
            )}

            {selectedFolderItem?.isFolderOwner ? (
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  setIsDeleteModalOpen(true);
                }}
                style={{
                  gap: "0.75rem",
                  padding: "14px 15px",
                  borderBottom: "1px solid #E3E6EA",
                }}
              >
                <DeleteIcon />
                <Typography
                  fontSize="1rem"
                  fontWeight="500"
                  style={{ cursor: "pointer" }}
                >
                  Delete
                </Typography>
              </MenuItem>
            ) : null}
          </>
        )}
      </Menu>
    </>
  );
};

export default SidebarTeamsFolders;
